import {
  useMemo,
  useState,
} from 'react';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useBackTarget } from '../../pricingJourney/navigation';
import { useQuery } from '@tanstack/react-query';

import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import PageSkeleton from '../../pricingJourney/components/Skeletons';
import { pageFrameSx } from '../../pricingJourney/components/PageHeader';

import MarginCenterEntityTrendPanel
  from '../components/MarginCenterEntityTrendPanel';

import CommercialQueryPanel
  from '../components/CommercialQueryPanel';

import {
  getMarginCenterCapabilities,
  getMarginCenterCustomerContext,
} from '../services/marginCenterService';

import CustomerProductsDecisionPanel
  from '../components/CustomerProductsDecisionPanel';

import CustomerEconomicStudyPanel
  from '../components/CustomerEconomicStudyPanel';

import CustomerPositionPanel
  from '../components/CustomerPositionPanel';

import CustomerKpiBreakdownModal, {
  type CustomerKpiBreakdownMetric,
} from '../components/CustomerKpiBreakdownModal';

import {
  MarginCenterContextMetricCard,
  MarginCenterEntityContextHeader,
} from '../components/MarginCenterEntityContextHeader';

import {
  formatMarginCenterCurrency as formatCurrency,
  formatMarginCenterNumber as formatNumber,
} from '../utils/numberFormat';

import type {
  MarginCenterCommercialQueryContext,
  MarginCenterComparisonMode,
  MarginCenterCustomerContextParams,
  MarginCenterTimeGrain,
} from '../types/marginCenter';
import { pricingNumberFormat } from '../../../modules/pricingJourney/formatNumber';


const COMPARISON_MODES: MarginCenterComparisonMode[] = [
  'previous_period',
  'year_over_year',
  'custom',
  'none',
];

const TIME_GRAINS: MarginCenterTimeGrain[] = [
  'day',
  'week',
  'month',
];


function readComparisonMode(
  value: string | null,
): MarginCenterComparisonMode {
  if (
    value
    && COMPARISON_MODES.includes(
      value as MarginCenterComparisonMode,
    )
  ) {
    return value as MarginCenterComparisonMode;
  }

  return 'previous_period';
}


function readTimeGrain(
  value: string | null,
): MarginCenterTimeGrain {
  if (
    value
    && TIME_GRAINS.includes(
      value as MarginCenterTimeGrain,
    )
  ) {
    return value as MarginCenterTimeGrain;
  }

  return 'week';
}

function formatPercentage(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${formatNumber(value)} %`;
}

function formatSignedPercentage(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${
    value > 0 ? '+' : ''
  }${formatNumber(value)} %`;
}


function formatSignedInteger(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${
    value > 0 ? '+' : ''
  }${pricingNumberFormat({
    maximumFractionDigits: 0,
  }).format(value)}`;
}


export default function MarginCenterCustomerPage() {
  const navigate = useNavigate();
  // PRC-027: «Volver» al origen real (Overview, Explorer, Optimización, Control comercial); sin origen, Explorer.
  const back = useBackTarget('/margin-center/explorer', 'Volver al Explorer');
  const { customerId } = useParams<{ customerId: string }>();
  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();

  const [
    kpiDetail,
    setKpiDetail,
  ] = useState<CustomerKpiBreakdownMetric | null>(
    null,
  );

  const queryParams = useMemo<MarginCenterCustomerContextParams>(
    () => {
      const comparisonMode = readComparisonMode(
        searchParams.get('comparison'),
      );

      return {
        start_date:
          searchParams.get('from') || undefined,

        end_date:
          searchParams.get('to') || undefined,

        comparison_mode: comparisonMode,

        comparison_start_date:
          comparisonMode === 'custom'
            ? searchParams.get('comparisonFrom') || undefined
            : undefined,

        comparison_end_date:
          comparisonMode === 'custom'
            ? searchParams.get('comparisonTo') || undefined
            : undefined,

        time_grain: readTimeGrain(
          searchParams.get('grain'),
        ),
      };
    },
    [searchParams],
  );

  const capabilitiesQuery = useQuery({
    queryKey: [
      'margin-center',
      'capabilities',
    ],
    queryFn:
      getMarginCenterCapabilities,
  });

  const customerQuery = useQuery({
    queryKey: [
      'margin-center',
      'customer',
      customerId,
      queryParams,
    ],
    queryFn: () =>
      getMarginCenterCustomerContext(
        customerId ?? '',
        queryParams,
      ),
    enabled: Boolean(customerId),
    placeholderData: (previousData) => previousData,
  });

  const context = customerQuery.data;

  const currencyCode =
    capabilitiesQuery.data
      ?.presentation
      .currency_code
    ?? null;

  const commercialQueryContext =
    useMemo<
      MarginCenterCommercialQueryContext | null
    >(
      () => {
        if (
          !context
          || context.status !== 'ok'
          || !context.customer
        ) {
          return null;
        }

        return {
          start_date:
            context.selection.start_date
            ?? undefined,

          end_date:
            context.selection.end_date
            ?? undefined,

          comparison_mode:
            context.selection.comparison_mode,

          comparison_start_date:
            context.selection.comparison_mode
              === 'custom'
              ? (
                context.selection
                  .comparison_start_date
                ?? undefined
              )
              : undefined,

          comparison_end_date:
            context.selection.comparison_mode
              === 'custom'
              ? (
                context.selection
                  .comparison_end_date
                ?? undefined
              )
              : undefined,

          time_grain:
            context.selection.time_grain,

          customer_id:
            context.customer.customer_id,
        };
      },
      [context],
    );

  const goBackToExplorer = () => {
    if (back.fromOrigin) {
      back.go();
      return;
    }

    const next = new URLSearchParams(searchParams);

    next.delete('returnTo');
    next.delete('customerLens');
    next.delete('relationshipSearch');
    next.delete('relationshipSort');
    next.delete('relationshipDirection');
    next.delete('relationshipCursor');
    next.delete('relationshipProduct');

    const query = next.toString();

    navigate(
      `/margin-center/explorer${query ? `?${query}` : ''}`,
    );
  };

  // PRC-031: forma de la ficha mientras carga, sin saltos.
  if (customerQuery.isLoading) {
    return (
      <Box sx={pageFrameSx}>
        <PageSkeleton variant="detail" bare padded={false} />
      </Box>
    );
  }

  if (customerQuery.isError) {
    return (
      <Box sx={pageFrameSx}>
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={goBackToExplorer}
          sx={{ mb: 2 }}
        >
          {back.label}
        </Button>

        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => customerQuery.refetch()}
            >
              Reintentar
            </Button>
          }
        >
          No se pudo cargar el contexto del cliente.
        </Alert>
      </Box>
    );
  }

  if (!context) {
    return null;
  }

  if (context.status === 'empty_dataset') {
    return (
      <Box sx={pageFrameSx}>
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={goBackToExplorer}
          sx={{ mb: 2 }}
        >
          {back.label}
        </Button>

        <Alert severity="info">
          Esta cuenta todavía no dispone de actividad transaccional.
        </Alert>
      </Box>
    );
  }

  if (
    context.status === 'customer_not_found'
    || !context.customer
    || !context.current
  ) {
    return (
      <Box sx={pageFrameSx}>
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={goBackToExplorer}
          sx={{ mb: 2 }}
        >
          {back.label}
        </Button>

        <Alert severity="warning">
          No se ha encontrado este cliente en la cuenta actual.
        </Alert>
      </Box>
    );
  }

  const {
    customer,
    current,
    comparison,
    changes,
    selection,
  } = context;

  const visibleAttributes = [
    customer.attributes.customer_segment,
    customer.attributes.industry_sector,
    customer.attributes.region,
    customer.attributes.country,
  ].filter(Boolean) as string[];

  const openRelationship = (
    productId: string,
  ) => {
    const next =
      new URLSearchParams(searchParams);

    next.delete('relationshipCursor');
    next.set(
      'relationshipProduct',
      productId,
    );

    setSearchParams(next);
  };

  return (
    <Box
      sx={pageFrameSx}
    >
      <Stack spacing={2.5}>
        <MarginCenterEntityContextHeader
          entityType="Cliente"
          label={customer.label}
          entityId={customer.customer_id}
          attributes={visibleAttributes}
          selection={selection}
          dataset={context.dataset}
          onBack={goBackToExplorer}
          backLabel={back.label}
        />

        {/* DUX-03D: acceso contextual; el cliente se conserva seleccionado. */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, bgcolor: '#0f172a', border: '1px solid rgba(56,189,248,0.22)' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Box>
              <Typography variant="overline" sx={{ color: '#38bdf8', fontWeight: 800 }}>Control comercial</Typography>
              <Typography variant="subtitle1" sx={{ color: '#f8fafc', fontWeight: 800 }}>Investigaciones de este cliente</Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>Casos propios y de sus relaciones, incluidos los cerrados. El período de análisis no limita su memoria.</Typography>
            </Box>
            <Button variant="contained" sx={{ textTransform: 'none', flexShrink: 0 }} onClick={() => {
              const next = new URLSearchParams({
                context: 'customer',
                customerId: customer.customer_id,
                returnTo: `/margin-center/customers/${encodeURIComponent(customer.customer_id)}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
              });
              navigate(`/margin-center/control-comercial?${next.toString()}`);
            }}>Ver seguimientos del cliente</Button>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(5, minmax(0, 1fr))',
            },
            gap: 1.25,
          }}
        >
          <MarginCenterContextMetricCard
            label="Ingresos"
            value={formatCurrency(
              current.revenue,
              currencyCode,
            )}
            change={
              changes
                ? `${formatSignedPercentage(
                    changes.revenue_delta_pct,
                  )} vs comparación`
                : undefined
            }
            changeValue={changes?.revenue_delta_pct}
            comparison={
              comparison
                ? formatCurrency(
                    comparison.revenue,
                    currencyCode,
                  )
                : undefined
            }
            onClick={() =>
              setKpiDetail('revenue')
            }
          />

          <MarginCenterContextMetricCard
            label="Margen"
            value={formatCurrency(
              current.margin_amount,
              currencyCode,
            )}
            change={
              changes?.margin_amount_delta_pct
                !== null
                && changes?.margin_amount_delta_pct
                  !== undefined
                ? `${formatSignedPercentage(
                    changes.margin_amount_delta_pct,
                  )} vs comparación`
                : undefined
            }
            changeValue={
              changes?.margin_amount_delta_pct
            }
            comparison={
              comparison
                ? formatCurrency(
                    comparison.margin_amount,
                    currencyCode,
                  )
                : undefined
            }
            onClick={() =>
              setKpiDetail('margin_amount')
            }
          />

          <MarginCenterContextMetricCard
            label="Margen %"
            value={formatPercentage(
              current.margin_percentage,
            )}
            change={
              changes?.margin_percentage_delta_points
                !== null
                && changes?.margin_percentage_delta_points
                  !== undefined
                ? `${
                    changes.margin_percentage_delta_points > 0
                      ? '+'
                      : ''
                  }${formatNumber(
                    changes.margin_percentage_delta_points,
                  )} pp vs comparación`
                : undefined
            }
            changeValue={
              changes?.margin_percentage_delta_points
            }
            comparison={
              comparison
                ? formatPercentage(
                    comparison.margin_percentage,
                  )
                : undefined
            }
            onClick={() =>
              setKpiDetail('margin')
            }
          />

          <MarginCenterContextMetricCard
            label="Productos activos"
            value={formatNumber(
              current.active_products,
            )}
            change={
              changes
                ? `${formatSignedInteger(
                    changes.active_products_delta,
                  )} vs comparación`
                : undefined
            }
            changeValue={
              changes?.active_products_delta
            }
            comparison={
              comparison
                ? formatNumber(
                    comparison.active_products,
                  )
                : undefined
            }
            onClick={() =>
              setKpiDetail(
                'active_products',
              )
            }
          />

          <MarginCenterContextMetricCard
            label="Unidades"
            value={formatNumber(current.units)}
            change={
              changes
                ? `${formatSignedPercentage(
                    changes.units_delta_pct,
                  )} vs comparación`
                : undefined
            }
            changeValue={changes?.units_delta_pct}
            comparison={
              comparison
                ? formatNumber(
                    comparison.units,
                  )
                : undefined
            }
            onClick={() =>
              setKpiDetail('units')
            }
          />
        </Box>

        <CustomerKpiBreakdownModal
          open={kpiDetail !== null}
          metric={
            kpiDetail ?? 'revenue'
          }
          customerId={
            customer.customer_id
          }
          startDate={
            queryParams.start_date
            ?? selection.start_date
          }
          endDate={
            queryParams.end_date
            ?? selection.end_date
          }
          comparisonMode={
            queryParams.comparison_mode
            ?? selection.comparison_mode
          }
          comparisonStartDate={
            queryParams.comparison_start_date
            ?? selection.comparison_start_date
          }
          comparisonEndDate={
            queryParams.comparison_end_date
            ?? selection.comparison_end_date
          }
          currencyCode={currencyCode}
          onClose={() =>
            setKpiDetail(null)
          }
          onOpenRelationship={
            openRelationship
          }
        />

        <CustomerEconomicStudyPanel
          customerId={customer.customer_id}
          startDate={
            queryParams.start_date
            ?? selection.start_date
          }
          endDate={
            queryParams.end_date
            ?? selection.end_date
          }
          comparisonMode={
            queryParams.comparison_mode
            ?? selection.comparison_mode
          }
          comparisonStartDate={
            queryParams.comparison_start_date
            ?? selection.comparison_start_date
          }
          comparisonEndDate={
            queryParams.comparison_end_date
            ?? selection.comparison_end_date
          }
          currencyCode={currencyCode}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              lg:
                'minmax(0, 2.15fr) minmax(320px, 1fr)',
            },
            gap: 1.5,
            alignItems: 'stretch',
          }}
        >
          <MarginCenterEntityTrendPanel
            series={context.series}
            selection={selection}
            currencyCode={currencyCode}
          />

          <CommercialQueryPanel
            context={commercialQueryContext}
            currencyCode={currencyCode}
            hasPartialBucket={
              context.series.some(
                (point) =>
                  point.is_partial_bucket,
              )
            }
            scopeLabel={
              `Cliente · ${customer.label}`
            }
            suggestedQuestions={[
              '¿Qué productos tienen mayor caída de ingresos?',
              '¿Dónde sube el precio y bajan las unidades?',
              '¿Qué relaciones pierden margen %?',
              '¿Dónde sube el coste unitario y cae el margen %?',
            ]}
            onOpenCustomer={() => undefined}
            onOpenRelationship={(
              _customerId,
              productId,
            ) =>
              openRelationship(productId)
            }
          />
        </Box>

        <CustomerPositionPanel
          customerId={customer.customer_id}
          startDate={
            queryParams.start_date
            ?? selection.start_date
          }
          endDate={
            queryParams.end_date
            ?? selection.end_date
          }
          comparisonMode={
            queryParams.comparison_mode
            ?? selection.comparison_mode
          }
          comparisonStartDate={
            queryParams.comparison_start_date
            ?? selection.comparison_start_date
          }
          comparisonEndDate={
            queryParams.comparison_end_date
            ?? selection.comparison_end_date
          }
          currencyCode={currencyCode}
        />

        <CustomerProductsDecisionPanel
          customerId={customer.customer_id}
          startDate={
            queryParams.start_date
            ?? selection.start_date
          }
          endDate={
            queryParams.end_date
            ?? selection.end_date
          }
          comparisonMode={
            queryParams.comparison_mode
            ?? selection.comparison_mode
          }
          comparisonStartDate={
            queryParams.comparison_start_date
            ?? selection.comparison_start_date
          }
          comparisonEndDate={
            queryParams.comparison_end_date
            ?? selection.comparison_end_date
          }
          timeGrain={
            queryParams.time_grain
            ?? selection.time_grain
          }
          currencyCode={currencyCode}
        />
      </Stack>
    </Box>
  );
}